import { useState, useEffect } from 'react';
import {
  Activity, CheckCircle2, XCircle, Loader2, Save,
  Zap, Shield, Bell, Video, Wifi, Scan, ClipboardList,
  ExternalLink, BarChart3, AlertTriangle, RefreshCw,
  Copy, Key
} from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';
import { toast } from 'react-toastify';

const EVENT_CATEGORIES = [
  { key: 'forwardDeviceHealth', label: 'Device Health', description: 'Online/offline transitions, latency changes', icon: Activity, color: '#10b981' },
  { key: 'forwardAccessEvents', label: 'Access Control', description: 'Door lock/unlock, access granted/denied', icon: Shield, color: '#60a5fa' },
  { key: 'forwardAlerts', label: 'Alerts', description: 'Monitoring alerts and threshold violations', icon: Bell, color: '#f59e0b' },
  { key: 'forwardRecordingChecks', label: 'Recording Assurance', description: 'Recording verification results and gaps', icon: Video, color: '#ef4444' },
  { key: 'forwardNetworkChanges', label: 'Network Changes', description: 'Port up/down, new device discovery, MAC changes', icon: Wifi, color: '#8b5cf6' },
  { key: 'forwardEntryMatches', label: 'Entry Matches', description: 'Facial recognition match/no-match events', icon: Scan, color: '#06b6d4' },
  { key: 'forwardAuditEvents', label: 'Audit Events', description: 'User login, config changes, admin actions', icon: ClipboardList, color: '#ec4899' },
];

export default function SplunkSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [stats, setStats] = useState(null);

  const [config, setConfig] = useState({
    configured: false,
    enabled: false,
    hecUrl: '',
    hecToken: '',
    hecTokenSet: false,
    indexName: 'serverli_ccm',
    sourceType: 'serverli:ccm:event',
    forwardDeviceHealth: true,
    forwardAccessEvents: true,
    forwardAlerts: true,
    forwardRecordingChecks: true,
    forwardNetworkChanges: true,
    forwardEntryMatches: true,
    forwardAuditEvents: true,
    webhookSecret: '',
  });

  useEffect(() => {
    loadConfig();
    loadStats();
  }, []);

  async function loadConfig() {
    try {
      const res = await fetchWithTenant('/api/splunk/config');
      const data = await res.json();
      if (data.configured) {
        setConfig(prev => ({ ...prev, ...data, hecToken: '' }));
      }
    } catch (err) {
      console.error('Failed to load Splunk config:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const res = await fetchWithTenant('/api/splunk/stats');
      const data = await res.json();
      if (data.configured) setStats(data);
    } catch (err) {
      console.error('Failed to load Splunk stats:', err);
    }
  }

  async function handleSave() {
    if (!config.hecUrl) {
      toast.error('HEC URL is required');
      return;
    }
    if (!config.configured && !config.hecToken) {
      toast.error('HEC Token is required for initial setup');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        enabled: config.enabled,
        hecUrl: config.hecUrl,
        indexName: config.indexName,
        sourceType: config.sourceType,
        forwardDeviceHealth: config.forwardDeviceHealth,
        forwardAccessEvents: config.forwardAccessEvents,
        forwardAlerts: config.forwardAlerts,
        forwardRecordingChecks: config.forwardRecordingChecks,
        forwardNetworkChanges: config.forwardNetworkChanges,
        forwardEntryMatches: config.forwardEntryMatches,
        forwardAuditEvents: config.forwardAuditEvents,
      };
      // Only send token if user entered a new one
      if (config.hecToken) payload.hecToken = config.hecToken;

      const res = await fetchWithTenant('/api/splunk/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Splunk configuration saved');
        setConfig(prev => ({ ...prev, configured: true, hecToken: '', hecTokenSet: true }));
        loadStats();
      } else {
        toast.error(data.error || 'Failed to save config');
      }
    } catch (err) {
      toast.error('Failed to save Splunk configuration');
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetchWithTenant('/api/splunk/test', { method: 'POST' });
      const data = await res.json();
      setTestResult(data);
      if (data.success) {
        toast.success('Splunk connection successful');
        loadStats();
      } else {
        toast.error('Connection failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setTestResult({ success: false, error: err.message });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  }

  function updateConfig(key, value) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: 'var(--text-secondary)' }}>
        <Loader2 size={20} className="spin" />
        <span>Loading Splunk configuration...</span>
      </div>
    );
  }

  const enabledCount = EVENT_CATEGORIES.filter(c => config[c.key]).length;

  return (
    <div className="splunk-settings">
      {/* Header with enable toggle */}
      <div className="splunk-header">
        <div className="splunk-header-left">
          <div className="splunk-logo">
            <BarChart3 size={24} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--text-primary)' }}>Splunk SIEM Integration</h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Forward security events to your Splunk instance for analytics and compliance
            </p>
          </div>
        </div>
        <label className="splunk-toggle-wrap">
          <span style={{ fontSize: '0.8rem', color: config.enabled ? 'var(--status-ok)' : 'var(--text-secondary)' }}>
            {config.enabled ? 'Enabled' : 'Disabled'}
          </span>
          <div
            className={`splunk-toggle ${config.enabled ? 'active' : ''}`}
            onClick={() => updateConfig('enabled', !config.enabled)}
          >
            <div className="splunk-toggle-thumb" />
          </div>
        </label>
      </div>

      {/* Stats bar (if configured) */}
      {stats && (
        <div className="splunk-stats-bar">
          <div className="splunk-stat">
            <span className="splunk-stat-value">{(stats.eventsSentCount || 0).toLocaleString()}</span>
            <span className="splunk-stat-label">Events Sent</span>
          </div>
          <div className="splunk-stat">
            <span className="splunk-stat-value" style={{ color: stats.errorCount > 0 ? 'var(--status-error)' : 'var(--text-primary)' }}>
              {stats.errorCount || 0}
            </span>
            <span className="splunk-stat-label">Errors</span>
          </div>
          <div className="splunk-stat">
            <span className="splunk-stat-value">
              {stats.lastForwardedAt ? new Date(stats.lastForwardedAt).toLocaleString() : 'Never'}
            </span>
            <span className="splunk-stat-label">Last Forwarded</span>
          </div>
          {stats.lastError && (
            <div className="splunk-stat splunk-stat-error">
              <AlertTriangle size={14} />
              <span className="splunk-stat-label" style={{ color: 'var(--status-error)' }}>{stats.lastError}</span>
            </div>
          )}
        </div>
      )}

      {/* Connection settings */}
      <div className="splunk-section">
        <h4 className="splunk-section-title">Connection</h4>
        <div className="splunk-form-grid">
          <div className="splunk-field">
            <label>HEC Endpoint URL</label>
            <input
              type="text"
              value={config.hecUrl}
              onChange={(e) => updateConfig('hecUrl', e.target.value)}
              placeholder="http://your-splunk:8088"
              spellCheck={false}
            />
          </div>
          <div className="splunk-field">
            <label>
              HEC Token
              {config.hecTokenSet && !config.hecToken && (
                <span style={{ fontSize: '0.7rem', color: 'var(--status-ok)', marginLeft: 8 }}>
                  <CheckCircle2 size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                  Set (leave blank to keep current)
                </span>
              )}
            </label>
            <input
              type="password"
              value={config.hecToken}
              onChange={(e) => updateConfig('hecToken', e.target.value)}
              placeholder={config.hecTokenSet ? '••••••••••••••••' : 'Enter HEC token'}
              spellCheck={false}
            />
          </div>
          <div className="splunk-field">
            <label>Index Name</label>
            <input
              type="text"
              value={config.indexName}
              onChange={(e) => updateConfig('indexName', e.target.value)}
              placeholder="serverli_ccm"
              spellCheck={false}
            />
          </div>
          <div className="splunk-field">
            <label>Source Type</label>
            <input
              type="text"
              value={config.sourceType}
              onChange={(e) => updateConfig('sourceType', e.target.value)}
              placeholder="serverli:ccm:event"
              spellCheck={false}
            />
          </div>
        </div>

        {/* Test connection */}
        <div className="splunk-test-row">
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleTest}
            disabled={testing || !config.configured}
            title={!config.configured ? 'Save configuration first' : ''}
          >
            {testing ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
            Test Connection
          </button>
          {testResult && (
            <span className={`splunk-test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
              {testResult.success ? 'Connection successful' : testResult.error}
            </span>
          )}
        </div>
      </div>

      {/* Event categories */}
      <div className="splunk-section">
        <h4 className="splunk-section-title">
          Event Forwarding
          <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 8 }}>
            {enabledCount} of {EVENT_CATEGORIES.length} active
          </span>
        </h4>
        <div className="splunk-categories">
          {EVENT_CATEGORIES.map(cat => {
            const Icon = cat.icon;
            const active = config[cat.key];
            return (
              <div
                key={cat.key}
                className={`splunk-category ${active ? 'active' : ''}`}
                onClick={() => updateConfig(cat.key, !active)}
              >
                <div className="splunk-category-icon" style={{ color: active ? cat.color : 'var(--text-secondary)' }}>
                  <Icon size={18} />
                </div>
                <div className="splunk-category-info">
                  <span className="splunk-category-label">{cat.label}</span>
                  <span className="splunk-category-desc">{cat.description}</span>
                </div>
                <div className={`splunk-category-check ${active ? 'active' : ''}`}>
                  {active ? <CheckCircle2 size={16} /> : <Circle size={16} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Webhook (Bidirectional) */}
      {config.configured && (
        <div className="splunk-section">
          <h4 className="splunk-section-title">Inbound Webhook</h4>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Configure Splunk alert actions to send webhooks back to CCM. Correlation alerts will create native CCM alerts.
          </p>
          <div className="splunk-form-grid">
            <div className="splunk-field">
              <label>Webhook URL</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/api/integrations/splunk/webhook`}
                  style={{ flex: 1, opacity: 0.8 }}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/api/integrations/splunk/webhook`);
                    toast.success('Webhook URL copied');
                  }}
                  title="Copy URL"
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div className="splunk-field">
              <label>Webhook Secret</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={config.webhookSecret || 'Not generated'}
                  style={{ flex: 1, fontFamily: config.webhookSecret ? 'monospace' : 'inherit', fontSize: config.webhookSecret ? '0.8rem' : '0.85rem', opacity: config.webhookSecret ? 1 : 0.5 }}
                />
                {config.webhookSecret && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      navigator.clipboard.writeText(config.webhookSecret);
                      toast.success('Webhook secret copied');
                    }}
                    title="Copy secret"
                  >
                    <Copy size={14} />
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={async () => {
                    try {
                      const res = await fetchWithTenant('/api/splunk/config', {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ generateWebhookSecret: true }),
                      });
                      const data = await res.json();
                      if (data.success) {
                        loadConfig();
                        toast.success('Webhook secret generated');
                      }
                    } catch (err) {
                      toast.error('Failed to generate secret');
                    }
                  }}
                  title="Generate new secret"
                >
                  <Key size={14} />
                </button>
              </div>
            </div>
          </div>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
            In Splunk, configure a Webhook alert action with this URL and include the secret as a Bearer token in the Authorization header.
          </p>
        </div>
      )}

      {/* Save button */}
      <div className="splunk-actions">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
          Save Configuration
        </button>
        {config.configured && config.hecUrl && (
          <a
            href={config.hecUrl.replace(/:\d+$/, ':8000')}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
          >
            <ExternalLink size={16} />
            Open Splunk
          </a>
        )}
      </div>
    </div>
  );
}
